import React, { useEffect, useState } from "react";
import api from "../utils/api.js";
import { URLS } from "../utils/urls.js";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { CircleDollarSign, Users, UserCheck } from "lucide-react";
import { useTopLoader } from "../context/TopLoaderContext";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    traffic: [],
    userGrowth: [],
    revenueGrowth: []
  });
  const [loading, setLoading] = useState(true);
  const { start, stop } = useTopLoader();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        start();
        setLoading(true);
        const res = await api.get(URLS.STATS);
        setStats(res.data); // Res is usually {data: ...} in this project's api.js
      } catch (err) {
        console.error("Failed to fetch dashboard stats:", err);
      } finally {
        setLoading(false);
        stop();
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="space-y-8">
      {/* Maintenance Banner */}
      {stats.maintenanceMode && (
        <div className="bg-red-100 border-l-4 border-red-500 p-4 mb-4 rounded-r-xl shadow-sm animate-pulse">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 font-bold">
                System is currently in MAINTENANCE MODE. Public APIs may be restricted.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-[#db8654] to-[#f3b183] p-6 text-white shadow-lg">
        <h2 className="text-3xl font-bold">Dashboard</h2>
        <p className="opacity-90 mt-1">Welcome to Tribe25 Admin Panel</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { 
            title: "Total Users", 
            value: stats.totalUsers.toLocaleString(), 
            icon: <Users /> 
          },
          { 
            title: "Active Subscriptions", 
            value: stats.activeSubscriptions.toLocaleString(), 
            icon: <UserCheck /> 
          },
          { 
            title: "Estimated Revenue", 
            value: `$${stats.totalRevenue.toLocaleString()}`, 
            icon: <CircleDollarSign /> 
          },
        ].map((item, i) => (
          <div
            key={i}
            className="group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">
                {item.title}
              </h3>
              <div className="p-3 rounded-xl bg-[#db8654]/10 text-[#db8654] group-hover:scale-110 transition">
                {item.icon}
              </div>
            </div>
            <p className="mt-4 text-3xl font-bold text-gray-800">
              {loading ? "..." : item.value}
            </p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Active Users */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="font-semibold text-gray-700 mb-4">
            Active Users (Last 7 Days)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={stats.traffic}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              />
              <Line
                type="monotone"
                dataKey="users"
                stroke="#db8654"
                strokeWidth={3}
                dot={{ r: 4, fill: "#db8654", strokeWidth: 2, stroke: "#fff" }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* User Growth */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="font-semibold text-gray-700 mb-4">
            User Growth (Monthly)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={stats.userGrowth}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              />
              <Line
                type="monotone"
                dataKey="users"
                stroke="#db8654"
                strokeWidth={3}
                dot={{ r: 4, fill: "#db8654", strokeWidth: 2, stroke: "#fff" }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="font-semibold text-gray-700 mb-4">
            Revenue Trend (Est.)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.revenueGrowth}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                cursor={{ fill: '#f3b183', opacity: 0.1 }}
              />
              <Bar
                dataKey="revenue"
                fill="#db8654"
                radius={[6, 6, 0, 0]}
                barSize={30}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
